import { Head, Link, router } from '@inertiajs/react';
import { type FormEvent, useState } from 'react';

import {
    Btn,
    Chip,
    DesignFooter,
    DesignHeader,
    Headshot,
    I,
    PhoneCTA,
    Pill,
    Stars,
    TrustBadge,
    UtilityBar,
} from '@/Components/Design';

type StateOpt = { slug: string; name: string };
type PracticeOpt = { slug: string; name: string };
type Filters = {
    q: string;
    state: string | null;
    city: string | null;
    practice: string | null;
    distance: number | null;
    free: boolean;
    languages: string[];
    minRating: number | null;
    accepting: boolean;
    sort: string;
};
type ServerCard = {
    id: number;
    slug: string;
    name: string;
    firm: string | null;
    city: string | null;
    state: string | null;
    stateAbbr: string | null;
    phone: string | null;
    yearsLicensed: number | null;
    languages: string[];
    freeConsultation: boolean;
    featured: boolean;
    reviewsAvg: number | null;
    reviewsCount: number;
    practiceAreas: Array<{ slug: string; name: string }>;
    bioShort: string | null;
    headshotKey: string | null;
    verified: boolean;
};
type Results = { hits: ServerCard[]; total: number; page: number; perPage: number };
type Props = {
    filters: Filters;
    options: { states: StateOpt[]; practiceAreas: PracticeOpt[] };
    results: Results;
};

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN';
}

function hueOf(slug: string): number {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
    return Math.abs(h) % 6;
}

function LawyerRow({ card }: { card: ServerCard }) {
    const initials = initialsOf(card.name);
    const hue = hueOf(card.slug);
    const url = `/${card.slug}`;
    return (
        <div data-card data-search-row style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 14, padding: 20, display: 'grid', gridTemplateColumns: '112px 1fr 240px', gap: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                {card.headshotKey ? (
                    <img src={`/cdn/lawyers/320/${card.headshotKey}.webp`} alt={card.name} style={{ width: 96, height: 96, borderRadius: 10, objectFit: 'cover' }} />
                ) : (
                    <Headshot initials={initials} hue={hue} size={96} radius={10} />
                )}
                {card.verified && (
                    <Pill tone="sage" style={{ fontSize: 10 }}>
                        <span style={{ display: 'inline-flex' }}>{I.check}</span> Verified
                    </Pill>
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: 22, lineHeight: 1.2 }}>
                            <Link href={url} style={{ color: 'var(--ink)', textDecoration: 'none' }}>{card.name}</Link>
                        </h3>
                        {card.yearsLicensed && <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{card.yearsLicensed} yrs licensed</span>}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 2 }}>
                        {card.firm}{card.firm && card.city ? ' · ' : ''}{card.city}{card.city && card.stateAbbr ? ', ' : ''}{card.stateAbbr}
                    </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {card.practiceAreas.slice(0, 4).map((a) => (
                        <Pill key={a.slug} tone="paper">{a.name}</Pill>
                    ))}
                </div>
                {card.bioShort && <p style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5, maxWidth: 560 }}>{card.bioShort}</p>}
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--ink-mute)', alignItems: 'center', flexWrap: 'wrap' }}>
                    {card.state && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--sage)' }}>{I.shield} Bar of {card.state}</span>
                    )}
                    {card.freeConsultation && <><span>·</span><span>Free consultation</span></>}
                    {card.languages.includes('es') && <><span>·</span><span>Habla español</span></>}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch', borderLeft: '1px solid var(--rule-soft)', paddingLeft: 24 }}>
                <Stars value={card.reviewsAvg ?? 0} reviews={card.reviewsCount} />
                {card.phone && <PhoneCTA number={card.phone} kind="secondary" size="md" full />}
                <Btn kind="primary" href={url} full>View profile</Btn>
            </div>
        </div>
    );
}

export default function Search({ filters, options, results }: Props) {
    const [q, setQ] = useState(filters.q);
    const [state, setState] = useState(filters.state ?? '');
    const [practice, setPractice] = useState(filters.practice ?? '');

    function submit(e: FormEvent) {
        e.preventDefault();
        router.get('/search', { q, state, practice }, { preserveScroll: true });
    }

    return (
        <>
            <Head>
                <title>{`Personal injury lawyers ${state ? `in ${options.states.find((s) => s.slug === state)?.name ?? ''}` : ''}`}</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Find a Lawyer" />

                <section data-search-section style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 56px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 16 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>Search</span>
                    </div></nav>

                    <div data-search-header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 32, marginBottom: 24 }}>
                        <div>
                            <h1 style={{ fontSize: 40, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 8 }}>
                                {results.total.toLocaleString()} personal injury lawyer{results.total === 1 ? '' : 's'}
                            </h1>
                            <div style={{ display: 'flex', gap: 18, fontSize: 13, color: 'var(--ink-soft)', alignItems: 'center', flexWrap: 'wrap' }}>
                                <TrustBadge tone="sage" icon={I.check} label="All bar-verified" />
                                {filters.state && <><span>·</span><span>{options.states.find((s) => s.slug === filters.state)?.name}</span></>}
                                {filters.practice && <><span>·</span><span>{options.practiceAreas.find((p) => p.slug === filters.practice)?.name}</span></>}
                            </div>
                        </div>
                        <PhoneCTA number="1-833-INJURY-1" kind="deep" size="md" />
                    </div>

                    <div data-search-layout style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
                        <aside data-search-sidebar style={{ width: 280, flexShrink: 0 }}>
                            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                <div style={{ padding: '6px 0 12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500 }}>Filters</div>
                                    <Link href="/search" style={{ background: 'transparent', border: 0, color: 'var(--ember)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Reset</Link>
                                </div>

                                <div data-filter-group style={{ padding: '18px 0', borderBottom: '1px solid var(--rule-soft)' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Keyword</div>
                                    <input
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Name, firm, city…"
                                        style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--rule)', borderRadius: 8, background: 'var(--card)', fontSize: 13, outline: 0 }}
                                    />
                                </div>

                                <div data-filter-group style={{ padding: '18px 0', borderBottom: '1px solid var(--rule-soft)' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>State</div>
                                    <select
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--rule)', borderRadius: 8, background: 'var(--card)', fontSize: 13, outline: 0 }}
                                    >
                                        <option value="">All states</option>
                                        {options.states.map((s) => (
                                            <option key={s.slug} value={s.slug}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div data-filter-group style={{ padding: '18px 0', borderBottom: '1px solid var(--rule-soft)' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Practice area</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        <Chip label="All" selected={practice === ''} onClick={() => setPractice('')} />
                                        {options.practiceAreas.slice(0, 6).map((p) => (
                                            <Chip key={p.slug} label={p.name} selected={practice === p.slug} onClick={() => setPractice(p.slug)} />
                                        ))}
                                    </div>
                                </div>

                                <Btn kind="primary" size="md" full style={{ marginTop: 14 }} type="submit">
                                    Apply filters
                                </Btn>
                            </form>
                        </aside>

                        <main data-search-main style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 10, marginBottom: 18 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: 'var(--ink-soft)' }}>
                                    <span>{results.total.toLocaleString()} attorney{results.total === 1 ? '' : 's'}</span>
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>Page {results.page}</div>
                            </div>

                            {results.hits.length === 0 ? (
                                <div style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 48, textAlign: 'center' }}>
                                    <div style={{ fontSize: 18, color: 'var(--ink-soft)', marginBottom: 8 }}>No attorneys match your filters.</div>
                                    <Link href="/search" style={{ color: 'var(--ember)', fontSize: 14, fontWeight: 600 }}>Clear all filters →</Link>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {results.hits.map((c) => (
                                        <LawyerRow key={c.id} card={c} />
                                    ))}
                                </div>
                            )}
                        </main>
                    </div>
                </section>

                <DesignFooter />
            </div>
        </>
    );
}
